/**
 * EPG Configuration Module
 *
 * Defines event options for all three marketing categories:
 * - Events: 6 event types (Education, HR, Tastings, Special, Sports, Outdoor)
 * - Calls & Emails: 3 communication types (Calls, Emails, Direct Mail)
 * - Drops: 5+ categories (Alcohol, Holidays, Office, Books, Event Driven)
 *
 * Based on desktop reference: event_config_extended.py
 *
 * Created: 2025-12-15
 * Phase: P2-T02, P2-T03, P2-T04 - Cell Editing & Event Selection
 * Updated: 2025-12-16 - Phase 2.4: Dynamic field definition loading
 */

import type { EventOption, FieldDefinition, FieldDefinitionOption } from '@shared/types/epg';

/**
 * ============================================
 * EVENTS CATEGORY OPTIONS
 * ============================================
 * 6 event types with color coding
 */

export const EVENT_TYPE_OPTIONS: EventOption[] = [
  // Education Events
  {
    id: 2001,
    label: 'Chamber Event',
    is_separator: false,
    color: '#3b82f6', // Blue
    category_type: 'education',
  },
  {
    id: 2002,
    label: 'Trade Show',
    is_separator: false,
    color: '#3b82f6',
    category_type: 'education',
  },
  {
    id: 2003,
    label: 'Conference',
    is_separator: false,
    color: '#3b82f6',
    category_type: 'education',
  },
  {
    id: 2004,
    label: 'Webinar',
    is_separator: false,
    color: '#3b82f6',
    category_type: 'education',
  },

  // Networking & Social
  {
    id: 2010,
    label: 'Networking',
    is_separator: false,
    color: '#10b981', // Green
    category_type: 'networking',
  },
  {
    id: 2011,
    label: 'Sponsorship',
    is_separator: false,
    color: '#10b981',
    category_type: 'networking',
  },

  // Speaking & Leadership
  {
    id: 2020,
    label: 'Speaking',
    is_separator: false,
    color: '#fbbf24', // Yellow
    category_type: 'leadership',
  },

  // Generic
  {
    id: 2099,
    label: 'Other',
    is_separator: false,
    color: '#6b7280', // Gray
    category_type: 'other',
  },
];

/**
 * ============================================
 * CALLS & EMAILS CATEGORY OPTIONS
 * ============================================
 * 3 communication types with sub-options
 */

export const CALLS_EMAILS_OPTIONS: EventOption[] = [
  // ===== CALLS =====
  {
    id: 3001,
    label: '----- CALLS -----',
    is_separator: true,
    color: null,
    category_type: null,
  },
  {
    id: 3002,
    label: 'Initial Contact',
    is_separator: false,
    color: '#0891b2', // Cyan
    category_type: 'call',
  },
  {
    id: 3003,
    label: 'Follow-up',
    is_separator: false,
    color: '#0891b2',
    category_type: 'call',
  },
  {
    id: 3004,
    label: 'Check-in',
    is_separator: false,
    color: '#0891b2',
    category_type: 'call',
  },
  {
    id: 3005,
    label: 'Renewal Discussion',
    is_separator: false,
    color: '#0891b2',
    category_type: 'call',
  },
  {
    id: 3006,
    label: 'Quarterly Business Review',
    is_separator: false,
    color: '#0891b2',
    category_type: 'call',
  },

  // ===== EMAILS =====
  {
    id: 3020,
    label: '----- EMAILS -----',
    is_separator: true,
    color: null,
    category_type: null,
  },
  {
    id: 3021,
    label: 'Welcome Email',
    is_separator: false,
    color: '#10b981', // Green
    category_type: 'email',
  },
  {
    id: 3022,
    label: 'Monthly Newsletter',
    is_separator: false,
    color: '#10b981',
    category_type: 'email',
  },
  {
    id: 3023,
    label: 'Event Invitation',
    is_separator: false,
    color: '#10b981',
    category_type: 'email',
  },
  {
    id: 3024,
    label: 'Case Study Email',
    is_separator: false,
    color: '#10b981',
    category_type: 'email',
  },
  {
    id: 3025,
    label: 'Service Update',
    is_separator: false,
    color: '#10b981',
    category_type: 'email',
  },
  {
    id: 3026,
    label: 'Renewal Reminder',
    is_separator: false,
    color: '#10b981',
    category_type: 'email',
  },
  {
    id: 3027,
    label: 'Holiday Greeting',
    is_separator: false,
    color: '#10b981',
    category_type: 'email',
  },

  // ===== DIRECT MAIL =====
  {
    id: 3040,
    label: '----- DIRECT MAIL -----',
    is_separator: true,
    color: null,
    category_type: null,
  },
  {
    id: 3041,
    label: 'Introduction Packet',
    is_separator: false,
    color: '#8b5cf6', // Purple
    category_type: 'mail',
  },
  {
    id: 3042,
    label: 'Annual Report',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'mail',
  },
  {
    id: 3043,
    label: 'Holiday Card',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'mail',
  },

  // Generic
  {
    id: 3099,
    label: 'Other',
    is_separator: false,
    color: '#6b7280', // Gray
    category_type: 'other',
  },
];

/**
 * ============================================
 * DROPS CATEGORY OPTIONS
 * ============================================
 * 5+ drop categories with 50+ individual items
 */

export const DROPS_OPTIONS: EventOption[] = [
  // ===== ALCOHOL =====
  {
    id: 1460,
    label: '---- Alcohol ----',
    is_separator: true,
    color: null,
    category_type: null,
  },
  {
    id: 1461,
    label: 'Wine - Stag (red)',
    is_separator: false,
    color: '#dc2626', // Red
    category_type: 'alcohol',
  },
  {
    id: 1462,
    label: 'Wine - Charity (white)',
    is_separator: false,
    color: '#dc2626',
    category_type: 'alcohol',
  },
  {
    id: 1463,
    label: 'QR Wine Drop',
    is_separator: false,
    color: '#dc2626',
    category_type: 'alcohol',
  },
  {
    id: 1464,
    label: 'Bourbon',
    is_separator: false,
    color: '#dc2626',
    category_type: 'alcohol',
  },
  {
    id: 1465,
    label: 'Tequila',
    is_separator: false,
    color: '#dc2626',
    category_type: 'alcohol',
  },
  {
    id: 1466,
    label: 'Mix Kit',
    is_separator: false,
    color: '#dc2626',
    category_type: 'alcohol',
  },

  // ===== HOLIDAYS =====
  {
    id: 1467,
    label: '---- Holidays ----',
    is_separator: true,
    color: null,
    category_type: null,
  },
  {
    id: 1468,
    label: 'Pecan Pie',
    is_separator: false,
    color: '#059669', // Green
    category_type: 'holidays',
  },
  {
    id: 1469,
    label: "King's Cake",
    is_separator: false,
    color: '#059669',
    category_type: 'holidays',
  },
  {
    id: 1470,
    label: 'Champagne',
    is_separator: false,
    color: '#059669',
    category_type: 'holidays',
  },

  // ===== FOR THE OFFICE =====
  {
    id: 1471,
    label: '---- For the Office ----',
    is_separator: true,
    color: null,
    category_type: null,
  },
  {
    id: 1472,
    label: 'Drop Breakfast',
    is_separator: false,
    color: '#f59e0b', // Amber
    category_type: 'office',
  },
  {
    id: 1473,
    label: 'Drop VooDoo Donuts',
    is_separator: false,
    color: '#f59e0b',
    category_type: 'office',
  },
  {
    id: 1474,
    label: 'Drop Kolaches',
    is_separator: false,
    color: '#f59e0b',
    category_type: 'office',
  },
  {
    id: 1475,
    label: 'Drop Breakfast Tacos',
    is_separator: false,
    color: '#f59e0b',
    category_type: 'office',
  },
  {
    id: 1476,
    label: 'Drop Coffee',
    is_separator: false,
    color: '#f59e0b',
    category_type: 'office',
  },
  {
    id: 1477,
    label: 'Drop Afternoon Snack',
    is_separator: false,
    color: '#f59e0b',
    category_type: 'office',
  },

  // ===== BOOKS & MAILERS =====
  {
    id: 1478,
    label: '--- Books & Mailers ----',
    is_separator: true,
    color: null,
    category_type: null,
  },
  {
    id: 1479,
    label: 'Zen Garden',
    is_separator: false,
    color: '#6366f1', // Indigo
    category_type: 'books',
  },
  {
    id: 1480,
    label: 'The Price We Pay',
    is_separator: false,
    color: '#6366f1',
    category_type: 'books',
  },
  {
    id: 1481,
    label: 'The One Minute Manager',
    is_separator: false,
    color: '#6366f1',
    category_type: 'books',
  },

  // ===== EVENT DRIVEN DROPS =====
  {
    id: 1482,
    label: '--- Event Driven Drops ----',
    is_separator: true,
    color: null,
    category_type: null,
  },
  {
    id: 1483,
    label: 'Horse Races',
    is_separator: false,
    color: '#8b5cf6', // Purple
    category_type: 'event',
  },
  {
    id: 1484,
    label: 'Kentucky Derby',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'event',
  },
  {
    id: 1485,
    label: 'Margarita Tasting',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'event',
  },
  {
    id: 1486,
    label: 'Sporting Clay Event',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'event',
  },
  {
    id: 1487,
    label: 'Polo Matches',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'event',
  },
  {
    id: 1488,
    label: 'Holiday HR Networking Event',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'event',
  },
  {
    id: 1489,
    label: 'Masters Event',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'event',
  },
  {
    id: 1490,
    label: "Men's Clay Court Event",
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'event',
  },
  {
    id: 1491,
    label: 'Elite Wine Tasting',
    is_separator: false,
    color: '#8b5cf6',
    category_type: 'event',
  },
];

/**
 * ============================================
 * CATEGORY CONFIGURATION
 * ============================================
 */

export const CATEGORY_OPTIONS_MAP: Record<string, EventOption[]> = {
  'Events': EVENT_TYPE_OPTIONS,
  'Calls & Emails': CALLS_EMAILS_OPTIONS,
  'Drops': DROPS_OPTIONS,
};

/**
 * Get options for a specific category
 */
export function getCategoryOptions(category: string): EventOption[] {
  const normalizedCategory = normalizeCategoryName(category);
  return CATEGORY_OPTIONS_MAP[normalizedCategory] || [];
}

/**
 * Get options for a category without separators (useful for validation)
 */
export function getCategoryOptionsWithoutSeparators(category: string): EventOption[] {
  return getCategoryOptions(category).filter((option) => !option.is_separator);
}

/**
 * Find option by ID across all categories
 */
export function findOptionById(optionId: number): EventOption | undefined {
  for (const options of Object.values(CATEGORY_OPTIONS_MAP)) {
    const found = options.find((opt) => opt.id === optionId);
    if (found) return found;
  }
  return undefined;
}

/**
 * Get option labels by IDs
 */
export function getOptionLabels(optionIds: number[]): string[] {
  return optionIds
    .map((id) => {
      const option = findOptionById(id);
      return option && !option.is_separator ? option.label : null;
    })
    .filter((label): label is string => label !== null);
}

/**
 * ============================================
 * CATEGORY COLORS (for UI styling)
 * ============================================
 */

export const CATEGORY_COLORS = {
  Events: {
    bg: '#a78bfa',        // Purple
    bgHover: '#9333ea40', // Purple with opacity
    text: '#e9d5ff',      // Light purple
    border: '#7e22ce',    // Dark purple
    glow: '0 0 10px rgba(147, 51, 234, 0.3)',
  },
  'Calls & Emails': {
    bg: '#6ee7b7',        // Green
    bgHover: '#10b98140', // Green with opacity
    text: '#d1fae5',      // Light green
    border: '#047857',    // Dark green
    glow: '0 0 10px rgba(16, 185, 129, 0.3)',
  },
  Drops: {
    bg: '#fb7185',        // Pink
    bgHover: '#f4338540', // Pink with opacity
    text: '#fce7f3',      // Light pink
    border: '#be123c',    // Dark pink
    glow: '0 0 10px rgba(244, 63, 94, 0.3)',
  },
};

/**
 * Get color configuration for a category
 */
export function getCategoryColors(category: string) {
  const normalizedCategory = normalizeCategoryName(category);
  return CATEGORY_COLORS[normalizedCategory as keyof typeof CATEGORY_COLORS] || CATEGORY_COLORS.Events;
}

/**
 * ============================================
 * PIPEDRIVE FIELD KEY MAPPINGS (Phase 2.4)
 * ============================================
 * Maps category names to their Pipedrive field keys for each month
 * Based on desktop EPG: event_config_extended.py
 */

export const FIELD_KEY_MAPPINGS = {
  Events: {
    JAN: 'bc5b330241a761e0411b44ab3b4f24211f69929d',
    FEB: 'a38db5e22740d9d41f3d61efa2a688c31f022a54',
    MAR: '2c8cee9ee8fa3c37692167625cb87a6a319cfa1a',
    APR: '7cf8b76eede651a0ef12129cb92b441adc193ade',
    MAY: '62d4f2f200b94db158b27b3d906c0c87cb24d7b9',
    JUN: '1d6af4f65d688bbe3cce8d631124074ffbf735b4',
    JUL: '9c06f44156944ebfbb0423ab273ddaf375afeb96',
    AUG: '7c7cc016821093372b653109760842a296f04c27',
    SEP: '5bdf515432be08aba9b27ec04837e0a77820bcc9',
    OCT: '174f11960e74b9f09403eafacc9b27de710ca92c',
    NOV: 'bb0b3c8ed12a1bf41bfe0e0bbd8d61b56176e4d9',
    DEC: '356c2aab8cf442ba0f08a3d73f1e605fd247ea6b',
  },
  'Calls & Emails': {
    JAN: 'e8b3880a0788d5ace62c8eac813d4c2c768f2792',
    FEB: '99eb646e6fbe4f4470119844fc4a0eb463e6d5b5',
    MAR: '4757298e70f331900c931a4cb900f1735ca72cc1',
    APR: '7cb5ab93e260169f069c4bffc5b0261028262b97',
    MAY: 'f09bfc82d89af86da129f1cc14bc1129f78f8b09',
    JUN: '8b4d7e54db4c45f6a42ad5fb3aaf757a71d06481',
    JUL: '63c5a4a2114e16e146306b70c16d7734f64e863a',
    AUG: 'f14e258445897486bb811fa4a23e2053dd3f0ebe',
    SEP: '31db045b4666b374999758b0fdc855fd04959140',
    OCT: 'e33feb84aca198ccf5ec1c613b28a6304b3a4978',
    NOV: '3d71c895df6f6d1f7674ffbb6dda2d3810e20d21',
    DEC: '5459f90462eaa7847244fa9d976b88a0385eb9b9',
  },
  Drops: {
    JAN: 'a201d845a71bdaaa41eaea446d82bac03498bb24',
    FEB: 'b3e32f5d513f4c6925fc3e1c8770d0d1ff801d02',
    MAR: '4cb0811e8837fb92c4890e4ca855d3a33581ec5d',
    APR: 'b8d8e33aadc84b2480e7cf128e37685e53fb8a7e',
    MAY: '8f76e39f3d2e6f1ab0d160b01e002dd8995970c8',
    JUN: '9d669e5ad42edbda29268b1ca70b8fe9b6685c1a',
    JUL: 'c484f98ecdb6673a0d6390236e906a050f80c7b7',
    AUG: '195f15bbfc17b1ab2877a901e4c9b2e487d5e0b8',
    SEP: '8e3af2ac0ed09c19266334512f609e6caf8210d1',
    OCT: '43b3674bcd4f5d0e5906f07ce4914766330d60b4',
    NOV: 'd9799d6e2915b7d64b43b692cffdb7289c3473a8',
    DEC: '1d60e809846499f6676849199a2d2a091ef1ce8d',
  },
} as const;

/**
 * ============================================
 * CATEGORY NAME NORMALIZATION
 * ============================================
 * The codebase uses two different naming conventions:
 * - UI components use 'Calls/Emails' (with slash)
 * - Config maps use 'Calls & Emails' (with ampersand)
 * This function normalizes to the config map convention.
 */

/**
 * Normalize category name to match config map keys
 * Converts 'Calls/Emails' to 'Calls & Emails' for consistent lookups
 */
export function normalizeCategoryName(category: string): string {
  const normalizations: Record<string, string> = {
    'Calls/Emails': 'Calls & Emails',
    'calls/emails': 'Calls & Emails',
    'Follow-Up': 'Calls & Emails',
    'follow-up': 'Calls & Emails',
  };
  return normalizations[category] || category;
}

/**
 * ============================================
 * DYNAMIC FIELD DEFINITION LOADING (Phase 2.4)
 * ============================================
 */

/**
 * Transform Pipedrive field definition options to EventOption format
 * Converts Pipedrive FieldDefinitionOption[] to UI-compatible EventOption[]
 *
 * @param fieldDefinition - Pipedrive field definition with options
 * @param category - Category name for color assignment
 * @returns Array of EventOption objects
 */
export function transformPipedriveOptions(
  fieldDefinition: FieldDefinition,
  category: string,
): EventOption[] {
  // Default color assignment based on category
  const categoryColors: Record<string, string> = {
    Events: '#3b82f6', // Blue
    'Calls & Emails': '#10b981', // Green
    Drops: '#f59e0b', // Amber
  };

  const defaultColor = categoryColors[category] || '#6b7280'; // Gray fallback

  return fieldDefinition.options.map((option: FieldDefinitionOption) => {
    // Detect separators (labels that start with dashes or are all caps)
    const isSeparator =
      option.label.startsWith('---') ||
      option.label.startsWith('----') ||
      option.label.match(/^-{3,}.*-{3,}$/);

    return {
      id: option.id,
      label: option.label,
      is_separator: Boolean(isSeparator),
      color: isSeparator ? null : defaultColor,
      category_type: isSeparator ? null : category.toLowerCase().replace(/\s+/g, '_'),
    };
  });
}

/**
 * Get event options for a specific category, optionally using dynamic Pipedrive data
 *
 * @param category - Category name ('Events', 'Calls & Emails', 'Drops')
 * @param fieldDefinitions - Optional Pipedrive field definitions from API
 * @param month - Optional month code (JAN, FEB, etc.) to get month-specific options
 * @returns Array of EventOption objects
 *
 * @example
 * // Use static fallback options
 * const options = getEventOptionsForCategory('Events');
 *
 * @example
 * // Use dynamic Pipedrive options
 * const fieldDefs = await fetchFieldDefinitions();
 * const options = getEventOptionsForCategory('Events', fieldDefs.fields, 'JAN');
 */
export function getEventOptionsForCategory(
  category: string,
  fieldDefinitions?: Record<string, FieldDefinition>,
  month?: string,
): EventOption[] {
  // Normalize category name (e.g., 'Calls/Emails' -> 'Calls & Emails')
  const normalizedCategory = normalizeCategoryName(category);

  // If field definitions provided, use dynamic Pipedrive data
  if (fieldDefinitions && month) {
    const fieldKey = FIELD_KEY_MAPPINGS[normalizedCategory as keyof typeof FIELD_KEY_MAPPINGS]?.[
      month as keyof (typeof FIELD_KEY_MAPPINGS)['Events']
    ];

    if (fieldKey && fieldDefinitions[fieldKey]) {
      return transformPipedriveOptions(fieldDefinitions[fieldKey], normalizedCategory);
    }
  }

  // Fall back to static options (using normalized category name)
  return CATEGORY_OPTIONS_MAP[normalizedCategory] || [];
}

/**
 * Get all field keys for a specific category across all months
 *
 * @param category - Category name ('Events', 'Calls & Emails', 'Drops')
 * @returns Array of Pipedrive field keys
 */
export function getFieldKeysForCategory(category: string): string[] {
  const normalizedCategory = normalizeCategoryName(category);
  const categoryMapping = FIELD_KEY_MAPPINGS[normalizedCategory as keyof typeof FIELD_KEY_MAPPINGS];
  if (!categoryMapping) return [];

  return Object.values(categoryMapping);
}

/**
 * Get field key for a specific category and month
 *
 * @param category - Category name ('Events', 'Calls & Emails', 'Drops')
 * @param month - Month code (JAN, FEB, etc.)
 * @returns Pipedrive field key or undefined
 */
export function getFieldKeyForCategoryMonth(category: string, month: string): string | undefined {
  const normalizedCategory = normalizeCategoryName(category);
  return FIELD_KEY_MAPPINGS[normalizedCategory as keyof typeof FIELD_KEY_MAPPINGS]?.[
    month as keyof (typeof FIELD_KEY_MAPPINGS)['Events']
  ];
}
